import { useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import PageHeader from "../../components/PageHeader"
import { useQuery } from "@apollo/client"
// queries
import { SplitButton } from "primereact/splitbutton"
import { ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE } from "./queries"

// Context
import { UserContext } from "../../context/userContext"

import { Button } from "primereact/button"

import LoaderFullPage from "../../components/Loaders"

import ParticipantSidebar from "../../components/sidebars/ParticipantSidebar"
import PageSidebarContent from "../../components/sidebars/PageSidebarContent"

//Hooks
import useLabels from "../../hooks/useLabels"
import GenericTable from "../../components/DataTable/GenericTable"
import FilterHeader from "../../components/DataTable/FilterHeader"
import { useFilters } from "../../components/Filters/useFilters"
import { Calendar } from "primereact/calendar"
import GLOBALS_STATUS from "../../config/globalsStatus"
import { handleOptsForFilter } from "../../components/DataTable/helpers"

import { parseDate } from "../../helpers/dateNormalization"
import { formatDate } from "../../components/DataTable/helpers"
import { navigateWithOrigin } from "../../helpers/navigation"

export default function RecoveryPlanningSession() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [dateRange, setDateRange] = useState(null)

  const currentYear = new Date().getFullYear()
  const yearRange = `2000:${currentYear + 10}`
  const userCtx = useContext(UserContext)

  const [loadingLabels, labels] = useLabels(["recovery_plan_engagement"])
  const navigate = useNavigate()
  const ID_TEMPLATES = ["recovery_plan_engagement"]
  const ID_BLOCKS = [
    "session",
    "referral",
    "session__assessment_recovery_capital",
    "session__recovery_plan",
  ]
  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    onFilterChange,
    clearGlobalFilter,
    clearAllFilters,
  } = useFilters()

  useQuery(ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE, {
    variables: {
      participant_id: userCtx.participant_id,
      organization_id: userCtx.session.user.organization_id,
      idTemplates: ID_TEMPLATES,
      idBlocks: ID_BLOCKS,
    },
    fetchPolicy: "no-cache",
    skip: !userCtx.session.user.staff_id,
    onCompleted: async (data) => {
      if (data.engagementsParticipant === null) {
        return setLoading(false)
      }
      const dataFormatted = JSON.parse(data.engagementsParticipant.data)

      const formattedData = _.map(dataFormatted, (template) => {
        const blocks = _.groupBy(template, "block_name")
        const session = _.isEmpty(blocks?.session) ? {} : blocks?.session[0]
        const session__recovery_plan = _.isEmpty(blocks?.session__recovery_plan)
          ? {}
          : blocks?.session__recovery_plan[0]
        const referral = _.isEmpty(blocks?.referral) ? {} : blocks?.referral[0]
        const recoveryCapitalScore = _.isEmpty(
          blocks?.session__assessment_recovery_capital
        )
          ? {}
          : blocks?.session__assessment_recovery_capital

        const cleanModifiedFields = _.pull(
          session__recovery_plan?.values?.block_modified_fields,
          "session_date",
          "date_created"
        )

        const plan_updated =
          _.isObject(cleanModifiedFields) && !_.isEmpty(cleanModifiedFields)
            ? "Yes"
            : "No"

        if (!_.isEmpty(session)) {
          const handleLocations = () => {
            const locationsList = session.values?.session__location_ids
              ? _.map(
                  session.values?.session__location_ids,
                  ({ label }) => label
                )
              : []
            return _.join(locationsList, ", ")
          }

          let actionButton
          _.includes(
            GLOBALS_STATUS.ACTIVE_STATUS,
            userCtx.participant_membership_status
          )
            ? (actionButton = (
                <SplitButton
                  label="View"
                  to={`/form/${session.idTemplate}/1/session.values.idStamp}`}
                  onClick={() => {
                    userCtx.changeParticipant(session.participant_id)
                    navigateWithOrigin(
                      navigate,
                      `/form/view/${session.idTemplate}/${session.idStamp}`
                    )
                  }}
                  model={[
                    {
                      label: "Edit",
                      command: () => {
                        navigate(
                          `/form/${session.idTemplate}/1/${session.idStamp}`
                        )
                      },
                    },
                  ]}
                />
              ))
            : (actionButton = (
                <Button
                  label="View"
                  to={`/form/${session.idTemplate}/1/session.values.idStamp}`}
                  onClick={() => {
                    userCtx.changeParticipant(session.participant_id)
                    navigateWithOrigin(
                      navigate,
                      `/form/view/${session.idTemplate}/${session.idStamp}`
                    )
                  }}
                />
              ))

          return {
            date: session?.values?.session_date
              ? parseDate(session.values.session_date)
              : null,
            locations: handleLocations(),
            length: `${session?.values?.session_length} minutes`,
            channel: session?.values?.session_channel,
            facilitating_staff: session?.staff_full_name,
            edit: actionButton,
            recovery_capital: recoveryCapitalScore[0]?.values.assessment_score
              ? recoveryCapitalScore[0].values.assessment_score
              : "-",
            referral_made: referral?.values === undefined ? "No" : "Yes",
            plan_updated: plan_updated,
          }
        }
      })

      setData(formattedData)
      setLoading(false)
    },
  })

  if (loading || loadingLabels) return <LoaderFullPage />
  const uniqueLocations = data ? handleOptsForFilter(data, "locations") : []
  const uniqueFacilitatingStaff = data
    ? handleOptsForFilter(data, "facilitating_staff")
    : []
  const filterConfig = [
    {
      label: "Date",
      value: dateRange,
      options: null,
      onChange: (e) => {
        setDateRange(e.value)
        onFilterChange("date", e.value)
      },
      onClear: () => {
        setDateRange(null)
        onFilterChange("date", null)
      },
      enableFilter: true,
      component: (
        <Calendar
          value={dateRange}
          onChange={(e) => {
            setDateRange(e.value)
            onFilterChange("date", e.value)
          }}
          placeholder="Select Date Range"
          selectionMode="range"
          dateFormat="mm/dd/yy"
          readOnlyInput
          numberOfMonths={2}
          showIcon
          showButtonBar
          yearNavigator
          yearRange={yearRange}
        />
      ),
    },
    {
      label: "Location",
      value: filters.locations?.value || "",
      options: uniqueLocations,
      onChange: (e) => onFilterChange("locations", e.value),
      onClear: () => onFilterChange("locations", null),
      enableFilter: true,
    },
    {
      label: "Facilitating Staff",
      value: filters.facilitating_staff?.value || "",
      options: uniqueFacilitatingStaff,
      onChange: (e) => onFilterChange("facilitating_staff", e.value),
      onClear: () => onFilterChange("facilitating_staff", null),
      enableFilter: true,
    },
  ]

  const columns = [
    {
      field: "date",
      header: "Date",
      sortable: true,
      body: (rowData) => (rowData?.date ? formatDate(rowData.date) : ""),
    },
    { field: "locations", header: "Location", sortable: true },
    { field: "channel", header: "Engagement Channel", sortable: true },
    {
      field: "facilitating_staff",
      header: "Facilitating Staff",
      sortable: true,
    },
    { field: "length", header: "Session Length", sortable: true },
    { field: "plan_updated", header: "Plan Updated", sortable: true },
    { field: "referral_made", header: "Referral Made", sortable: true },
    { field: "edit", header: "Action" },
  ]

  const handleClearAllFilters = () => {
    clearAllFilters()
    setDateRange(null)
  }

  return (
    <>
      <PageSidebarContent>
        <ParticipantSidebar />
      </PageSidebarContent>
      <div className="main-content">
        <div>
          <div>
            <PageHeader
              title={labels.recovery_plan_engagement}
              actions={
                _.includes(
                  GLOBALS_STATUS.ACTIVE_STATUS,
                  userCtx.participant_membership_status
                )
                  ? [
                      {
                        label: `New ${labels.recovery_plan_engagement}`,
                        to: "/form/recovery_plan_engagement/1",
                      },
                    ]
                  : []
              }
            />
            <GenericTable
              data={data}
              columns={columns}
              filters={filters}
              header={
                <FilterHeader
                  filters={filterConfig}
                  globalFilterValue={globalFilterValue}
                  onGlobalFilterChange={onGlobalFilterChange}
                  clearGlobalFilter={clearGlobalFilter}
                  clearAllFilters={handleClearAllFilters}
                />
              }
              paginator
              rows={50}
            />
          </div>
        </div>
      </div>
    </>
  )
}
